import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import VideoStatusPoller from './VideoStatusPoller';
import { WebSocketContext } from '../../contexts/WebSocketContext';
import './VideoGallery.css';

const VideoGallery = ({ token }) => {
  const socket = useContext(WebSocketContext);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all'); // all, complete, pending, failed

  const BASE_URL = `${process.env.REACT_APP_API_URL || 'http://localhost:10000'}/api/videos`;

  useEffect(() => {
    if (token) {
      fetchVideos();
    }
  }, [token]);

  // WebSocket listener for real-time video updates
  useEffect(() => {
    if (!socket) return;

    const handleWebSocketMessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('📨 WebSocket message received:', data);

        if (data.type === 'video_complete') {
          console.log('✅ Video complete event - refreshing gallery');
          // Refresh videos when a new video is completed
          fetchVideos();
        }
      } catch (err) {
        console.error('Error parsing WebSocket message:', err);
      }
    };

    socket.addEventListener('message', handleWebSocketMessage);

    return () => {
      socket.removeEventListener('message', handleWebSocketMessage);
    };
  }, [socket]);

  const fetchVideos = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await axios.get(BASE_URL, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.status === 200) {
        setVideos(response.data.videos);
      }
    } catch (err) {
      console.error('Error fetching videos:', err);
      if (err.response?.status === 401) {
        setError('Session expired. Please log in again.');
      } else {
        setError('Failed to load videos.');
      }
    } finally {
      setLoading(false);
    }
  };

  const filteredVideos = videos.filter(video => {
    if (filter === 'all') return true;
    return video.status === filter;
  });

  const handleVideoUpdate = (updatedVideo) => {
    setVideos(prevVideos =>
      prevVideos.map(v =>
        v.jobId === updatedVideo.jobId ? { ...v, ...updatedVideo } : v
      )
    );
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { emoji: '⏳', text: 'Pending', color: '#FFA726' },
      processing: { emoji: '🎬', text: 'Processing', color: '#42A5F5' },
      complete: { emoji: '✅', text: 'Complete', color: '#66BB6A' },
      failed: { emoji: '❌', text: 'Failed', color: '#EF5350' },
    };
    return badges[status] || badges.pending;
  };

  if (!token) {
    return (
      <div className="video-gallery-container">
        <div className="empty-state">
          <h2>🔒 Please log in</h2>
          <p>You need to be logged in to view your video gallery.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="video-gallery-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading videos...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="video-gallery-container">
        <div className="error-state">
          <h2>⚠️ Error</h2>
          <p>{error}</p>
          <button onClick={fetchVideos} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="video-gallery-container">
      <div className="gallery-header">
        <h2>🎬 Video Gallery</h2>
        <button onClick={fetchVideos} className="refresh-button">
          🔄 Refresh
        </button>
      </div>

      <div className="filter-bar">
        <button
          className={filter === 'all' ? 'active' : ''}
          onClick={() => setFilter('all')}
        >
          All ({videos.length})
        </button>
        <button
          className={filter === 'complete' ? 'active' : ''}
          onClick={() => setFilter('complete')}
        >
          ✅ Complete ({videos.filter(v => v.status === 'complete').length})
        </button>
        <button
          className={filter === 'pending' ? 'active' : ''}
          onClick={() => setFilter('pending')}
        >
          ⏳ Pending ({videos.filter(v => v.status === 'pending').length})
        </button>
        <button
          className={filter === 'processing' ? 'active' : ''}
          onClick={() => setFilter('processing')}
        >
          🎬 Processing ({videos.filter(v => v.status === 'processing').length})
        </button>
        <button
          className={filter === 'failed' ? 'active' : ''}
          onClick={() => setFilter('failed')}
        >
          ❌ Failed ({videos.filter(v => v.status === 'failed').length})
        </button>
      </div>

      {filteredVideos.length === 0 ? (
        <div className="empty-state">
          <h3>No videos found</h3>
          <p>
            {filter === 'all'
              ? 'Start by generating your first video!'
              : `No ${filter} videos at the moment.`}
          </p>
        </div>
      ) : (
        <div className="videos-grid">
          {filteredVideos.map(video => {
            const badge = getStatusBadge(video.status);
            return (
              <div key={video.jobId} className="video-card">
                <div className="video-card-header">
                  <h3>{video.petName}</h3>
                  <span
                    className="status-badge"
                    style={{ background: badge.color }}
                  >
                    {badge.emoji} {badge.text}
                  </span>
                </div>

                <div className="video-card-info">
                  <p><strong>Breed:</strong> {video.breed}</p>
                  <p><strong>Emotion:</strong> {video.emotion}</p>
                  <p><strong>Created:</strong> {new Date(video.createdAt).toLocaleString()}</p>
                </div>

                {video.status === 'complete' && video.videoUrl && (
                  <div className="video-player-wrapper">
                    <video
                      controls
                      className="video-player"
                      poster="/video-thumbnail.png"
                    >
                      <source src={video.videoUrl} type="video/mp4" />
                      Your browser does not support video playback.
                    </video>
                    {video.transcript && (
                      <div className="transcript">
                        <strong>💬 Transcript:</strong>
                        <p>"{video.transcript}"</p>
                      </div>
                    )}
                    <a
                      href={video.videoUrl}
                      download={`${video.petName}-${video.jobId}.mp4`}
                      className="download-button"
                    >
                      ⬇️ Download Video
                    </a>
                  </div>
                )}

                {(video.status === 'pending' || video.status === 'processing') && (
                  <VideoStatusPoller
                    jobId={video.jobId}
                    token={token}
                    onUpdate={handleVideoUpdate}
                  />
                )}

                {video.status === 'failed' && (
                  <div className="error-message">
                    <p>❌ Video generation failed</p>
                    {video.errorMessage && <small>{video.errorMessage}</small>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default VideoGallery;
